<template>
  <div
    class="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:py-10 xl:px-8"
  >
    <header :class="hasSearched ? 'mb-6' : 'mx-auto mb-8 max-w-2xl text-center'">
      <nav aria-label="Tootekategooria" class="mb-5">
        <ul
          class="inline-flex gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm"
        >
          <li v-for="tab in tabs" :key="tab.route">
            <router-link
              :to="{ name: tab.route }"
              :class="[
                'inline-flex rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                tab.category === category
                  ? 'bg-brand text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-ink',
              ]"
              :aria-current="tab.category === category ? 'page' : null"
            >
              {{ tab.title }}
            </router-link>
          </li>
        </ul>
      </nav>
      <h1 class="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
        {{ config.title }}
      </h1>
      <p class="mt-1.5 text-sm text-slate-500 sm:text-base">
        {{ config.intro }}
      </p>
    </header>

    <div :class="layoutClasses">
      <!-- Search filters -->
      <aside :class="sidebarClasses">
        <div class="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div
            class="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4"
          >
            <h2 class="flex items-center gap-2 text-base font-semibold text-ink">
              <svg
                class="h-5 w-5 text-brand"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.75"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"
                />
              </svg>
              Otsing
            </h2>
            <button
              v-if="hasSearched"
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-brand hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand lg:hidden"
              :aria-expanded="filtersVisible ? 'true' : 'false'"
              aria-controls="catalog-search-form"
              @click="toggleFilters"
            >
              <span>{{ filtersVisible ? "Peida" : "Näita" }} filtreid</span>
              <svg
                class="h-3 w-3 transition-transform"
                viewBox="0 0 10 6"
                fill="none"
                aria-hidden="true"
                :class="{ 'rotate-180': filtersVisible }"
              >
                <path
                  d="M1 1l4 4 4-4"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <form
            v-show="!hasSearched || filtersVisible"
            id="catalog-search-form"
            class="space-y-6 p-5"
            @submit.prevent="onSearch"
          >
            <component
              :is="config.form"
              v-model="filters"
              :brand-options="brandOptions"
              :compact="hasSearched"
            />

            <div class="space-y-3">
              <button
                type="submit"
                class="flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70"
                :disabled="loading"
              >
                <svg
                  v-if="loading"
                  class="h-4 w-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-90"
                    fill="currentColor"
                    d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
                  />
                </svg>
                <svg
                  v-else
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2.25"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
                Otsi
              </button>
              <p v-if="!hasSearched" class="text-center text-sm text-slate-500">
                Täida filtrid ja vajuta „Otsi“, et laadida tulemused.
              </p>
            </div>
          </form>

          <p
            v-if="hasSearched && !filtersVisible"
            class="px-5 py-4 text-center text-sm text-slate-500"
          >
            Filtrid on peidetud. Ava need nupuga „Näita filtreid“.
          </p>
        </div>
      </aside>

      <!-- Search results -->
      <section
        v-if="hasSearched"
        class="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
        aria-labelledby="results-heading"
      >
        <div
          class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-slate-200 px-5 py-4"
        >
          <h2 id="results-heading" class="text-lg font-semibold text-ink">
            Otsingu tulemused
          </h2>
          <p
            v-if="!loading && !error"
            class="text-sm text-slate-500"
            aria-live="polite"
          >
            {{ resultCountLabel }}
          </p>
        </div>

        <!-- Loading -->
        <div v-if="loading" aria-busy="true">
          <p class="sr-only" role="status">Laen andmeid…</p>
          <div
            v-for="n in 6"
            :key="n"
            class="flex animate-pulse items-center gap-4 border-b border-slate-100 px-5 py-4 last:border-b-0"
            aria-hidden="true"
          >
            <div class="h-[72px] w-[72px] shrink-0 rounded-lg bg-slate-100"></div>
            <div class="flex-1 space-y-2.5">
              <div class="h-4 w-3/4 rounded bg-slate-100"></div>
              <div class="h-3 w-1/3 rounded bg-slate-100"></div>
            </div>
            <div class="hidden w-24 space-y-2 sm:block">
              <div class="ml-auto h-4 w-20 rounded bg-slate-100"></div>
              <div class="ml-auto h-3 w-16 rounded bg-slate-100"></div>
            </div>
          </div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="p-5">
          <div
            role="alert"
            class="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          >
            <svg
              class="mt-0.5 h-5 w-5 shrink-0 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.75"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
            <div>
              <p class="font-semibold">Andmete laadimine ebaõnnestus</p>
              <p class="mt-1">{{ error }}</p>
              <button
                type="button"
                class="mt-3 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200 transition hover:bg-red-100"
                @click="onSearch"
              >
                Proovi uuesti
              </button>
            </div>
          </div>
        </div>

        <!-- Results list -->
        <template v-else-if="displayedItems.length">
          <div
            class="hidden border-b border-slate-200 bg-slate-50 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 md:grid md:grid-cols-[72px_72px_minmax(0,1fr)_100px_100px] md:gap-x-4 xl:grid-cols-[80px_88px_minmax(0,1fr)_112px_112px]"
            aria-hidden="true"
          >
            <div>Artikli ID</div>
            <div>Pilt</div>
            <div>Toode</div>
            <div class="text-right">Netohind</div>
            <div class="text-right">Hind koos KM</div>
          </div>

          <ul role="list" class="divide-y divide-slate-100">
            <li
              v-for="(item, index) in displayedItems"
              :key="item.ArticleId || index"
            >
              <component
                :is="item.ArticleId ? 'router-link' : 'div'"
                :to="
                  item.ArticleId
                    ? {
                        name: config.detailRoute,
                        params: { articleId: item.ArticleId },
                      }
                    : undefined
                "
                class="group grid grid-cols-[72px_minmax(0,1fr)] gap-x-4 gap-y-2 px-5 py-4 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:bg-brand-50/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand md:grid-cols-[72px_72px_minmax(0,1fr)_100px_100px] md:items-center md:gap-y-0 xl:grid-cols-[80px_88px_minmax(0,1fr)_112px_112px]"
                :aria-label="`${item.ArticleText} – ${formatNetPrice(
                  item.NetPrice
                )}`"
              >
                <!-- Image: always rendered, no hover needed -->
                <div
                  class="thumb relative col-start-1 row-span-2 row-start-1 h-[72px] w-[72px] overflow-hidden rounded-lg border border-slate-200 bg-white md:col-start-2 md:row-span-1 xl:h-[88px] xl:w-[88px]"
                >
                  <svg
                    class="thumb-fallback absolute inset-0 m-auto h-8 w-8 text-slate-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="4.5" />
                    <circle cx="12" cy="12" r="1.25" />
                  </svg>
                  <img
                    v-if="item.ImageId"
                    :src="buildImageUrl(item.ImageId)"
                    alt=""
                    width="88"
                    height="88"
                    decoding="async"
                    class="relative h-full w-full object-contain p-1"
                    @load="onThumbLoad"
                    @error="onThumbError"
                  />
                </div>

                <div class="col-start-2 row-start-1 min-w-0 md:col-start-3">
                  <p
                    class="text-[15px] font-semibold leading-snug text-ink transition-colors group-hover:text-brand-600"
                  >
                    {{ item.ArticleText }}
                  </p>
                  <p class="mt-1 text-sm text-slate-500">
                    {{ item.BrandName }} • {{ groupLabel(item) }}
                  </p>
                </div>

                <div
                  class="col-start-2 row-start-2 flex items-end gap-4 md:contents"
                >
                  <p
                    class="mr-auto self-end text-xs text-slate-500 tabular-nums md:col-start-1 md:row-start-1 md:mr-0 md:self-auto md:text-sm md:font-medium md:text-slate-700"
                  >
                    <span class="md:hidden">ID </span>{{ item.ArticleId }}
                  </p>
                  <div class="text-right md:col-start-4 md:row-start-1">
                    <p
                      class="text-[11px] font-medium uppercase tracking-wide text-slate-400 md:hidden"
                    >
                      Netohind
                    </p>
                    <p
                      class="whitespace-nowrap text-base font-bold text-brand-600 tabular-nums"
                    >
                      {{ formatNetPrice(item.NetPrice) }}
                    </p>
                  </div>
                  <div class="text-right md:col-start-5 md:row-start-1">
                    <p
                      class="text-[11px] font-medium uppercase tracking-wide text-slate-400 md:hidden"
                    >
                      Koos KM
                    </p>
                    <p
                      class="whitespace-nowrap text-sm font-semibold text-slate-600 tabular-nums md:text-[15px]"
                    >
                      {{ formatVatPrice(item.NetPrice) }}
                    </p>
                  </div>
                </div>
              </component>
            </li>
          </ul>

          <div
            v-if="hasMoreItems"
            ref="loadSentinel"
            class="flex items-center justify-center gap-2 border-t border-slate-100 py-5 text-sm text-slate-500"
            aria-hidden="true"
          >
            <svg
              class="h-4 w-4 animate-spin text-brand"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-90"
                fill="currentColor"
                d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
              />
            </svg>
            Laen rohkem tulemusi…
          </div>
        </template>

        <!-- Empty -->
        <div v-else class="px-6 py-16 text-center">
          <svg
            class="mx-auto h-12 w-12 text-slate-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.25"
            stroke="currentColor"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <circle cx="12" cy="12" r="4.5" />
            <circle cx="12" cy="12" r="1.25" />
          </svg>
          <p class="mt-4 text-base font-semibold text-ink">
            Otsingu tulemusi ei leitud.
          </p>
          <p class="mt-1 text-sm text-slate-500">
            Proovi muuta otsingu parameetreid.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { NET_PRICE_ADJUSTMENT } from "@/config/pricing";
import { matchesCategory, wheelGroupLabel } from "@/config/catalog";
import {
  buildTyreQuery,
  createTyreMatcher,
  emptyTyreFilters,
} from "@/catalog/tyreFilters";
import {
  buildWheelQuery,
  createWheelMatcher,
  emptyWheelFilters,
} from "@/catalog/wheelFilters";
import TyreFilterForm from "@/components/catalog/TyreFilterForm.vue";
import WheelFilterForm from "@/components/catalog/WheelFilterForm.vue";

// Both tabs share this page; the category decides the form, the API query
// and the hidden tyre/wheel filter applied on top of the user's filters.
const CATEGORIES = {
  tyres: {
    route: "Rehvid",
    detailRoute: "RehviDetail",
    title: "Rehvid",
    intro: "Otsi rehve mõõdu, tootja või mudeli järgi.",
    form: TyreFilterForm,
    emptyFilters: emptyTyreFilters,
    buildQuery: buildTyreQuery,
    createMatcher: createTyreMatcher,
  },
  wheels: {
    route: "Veljed",
    detailRoute: "VeljeDetail",
    title: "Veljed",
    intro: "Otsi velgi mõõdu, puurimuse, tootja või mudeli järgi.",
    form: WheelFilterForm,
    emptyFilters: emptyWheelFilters,
    buildQuery: buildWheelQuery,
    createMatcher: createWheelMatcher,
  },
};

export default {
  name: "SearchForm",
  props: {
    category: {
      type: String,
      default: "tyres",
      validator: (value) => value in CATEGORIES,
    },
  },
  data() {
    return {
      filters: CATEGORIES[this.category].emptyFilters(),
      hasSearched: false,
      displayedCount: 30,
      observer: null,
      filtersVisible: true,
      isSmallScreen: false,
      viewportMediaQuery: null,
      tabs: Object.entries(CATEGORIES).map(([category, config]) => ({
        category,
        route: config.route,
        title: config.title,
      })),
    };
  },
  mounted() {
    this.setupViewportListener();
    this.resetPagination();
    const savedSearch = this.applySavedSearch();
    const savedQuery = savedSearch?.lastQueryKey || "";
    const currentQuery = this.buildQueryParams();
    window.addEventListener("scroll", this.handleScroll, { passive: true });

    if (savedSearch?.hasSearched) {
      this.hasSearched = true;
      const forceFetch =
        !this.hasStoreArticles || savedQuery !== currentQuery;
      this.ensureArticles(forceFetch)
        .catch(() => {})
        .finally(() => {
          this.resetPagination();
          this.setupObserver();
          this.saveCurrentSearch();
          this.applyViewportFilterState();
        });
    } else {
      this.hasSearched = false;
      this.filtersVisible = true;
      this.setupObserver();
    }
  },
  computed: {
    config() {
      return CATEGORIES[this.category];
    },
    items() {
      return this.$store.getters["articles/articles"];
    },
    // The API returns tyres and wheels together, so each tab keeps only its
    // own category, even when the form is empty.
    categoryItems() {
      return this.items.filter((article) =>
        matchesCategory(article, this.category)
      );
    },
    loading() {
      return this.$store.getters["articles/isLoading"];
    },
    error() {
      return this.$store.getters["articles/error"];
    },
    hasStoreArticles() {
      return this.$store.getters["articles/hasArticles"];
    },
    brandOptions() {
      const values = new Set();
      this.categoryItems.forEach((item) => {
        if (item?.BrandName) {
          values.add(item.BrandName);
        }
      });
      if (this.filters.tootja && !values.has(this.filters.tootja)) {
        values.add(this.filters.tootja);
      }
      const sorted = Array.from(values).sort((a, b) => a.localeCompare(b));
      return [
        { label: "Kõik tootjad", value: "" },
        ...sorted.map((name) => ({ label: name, value: name })),
      ];
    },
    filteredItems() {
      if (!this.hasSearched) {
        return [];
      }
      if (!this.hasStoreArticles) {
        return [];
      }

      const matches = this.config.createMatcher(this.filters);
      return this.categoryItems.filter(matches);
    },
    displayedItems() {
      return this.filteredItems.slice(0, this.displayedCount);
    },
    hasMoreItems() {
      return this.filteredItems.length > this.displayedItems.length;
    },
    resultCountLabel() {
      const count = this.filteredItems.length;
      const formatted = new Intl.NumberFormat("et-EE").format(count);
      return count === 1 ? `${formatted} toode` : `${formatted} toodet`;
    },
    layoutClasses() {
      if (this.hasSearched) {
        return "grid items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)] xl:gap-8";
      }
      return "mx-auto w-full max-w-2xl";
    },
    sidebarClasses() {
      if (this.hasSearched) {
        return "min-w-0 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:rounded-2xl";
      }
      return "min-w-0";
    },
  },
  methods: {
    groupLabel(item) {
      return wheelGroupLabel(item) || item.MainGroupName;
    },
    onThumbLoad(event) {
      event.target.parentElement?.classList.add("is-loaded");
    },
    onThumbError(event) {
      event.target.style.display = "none";
    },
    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
      if (this.filtersVisible) {
        this.$nextTick(() => {
          this.setupObserver();
        });
      }
    },
    setupViewportListener() {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        this.isSmallScreen = false;
        return;
      }
      const query = window.matchMedia("(max-width: 1023px)");
      this.viewportMediaQuery = query;
      this.isSmallScreen = query.matches;
      const handler = this.handleViewportChange;
      if (typeof query.addEventListener === "function") {
        query.addEventListener("change", handler);
      } else if (typeof query.addListener === "function") {
        query.addListener(handler);
      }
      this.applyViewportFilterState();
    },
    handleViewportChange(event) {
      this.isSmallScreen = event.matches;
      this.applyViewportFilterState();
    },
    applyViewportFilterState() {
      if (!this.hasSearched) {
        this.filtersVisible = true;
        return;
      }
      this.filtersVisible = !this.isSmallScreen;
    },
    async ensureArticles(force = false) {
      const queryKey = this.buildQueryParams();
      try {
        await this.$store.dispatch("articles/fetchArticles", {
          queryKey,
          force,
        });
      } catch (e) {
        // error state handled via Vuex; no-op here to retain UI messaging
      }
    },
    onSearch() {
      this.hasSearched = true;
      return Promise.all([this.ensureArticles(true)])
        .catch(() => {})
        .finally(() => {
          this.resetPagination();
          this.setupObserver();
          this.saveCurrentSearch();
          this.applyViewportFilterState();
        });
    },
    buildQueryParams() {
      return this.config.buildQuery(this.filters);
    },
    buildImageUrl(imageId) {
      return `https://saurusrehv-backend.azurewebsites.net/api/ArticleImages/${imageId}`;
    },
    saveCurrentSearch() {
      this.$store.dispatch("articles/saveSearch", {
        category: this.category,
        search: {
          filters: { ...this.filters },
          hasSearched: this.hasSearched,
          lastQueryKey: this.buildQueryParams(),
        },
      });
    },
    applySavedSearch() {
      const saved = this.$store.getters["articles/savedSearch"](this.category);
      if (!saved) return null;

      this.filters = { ...this.config.emptyFilters(), ...saved.filters };
      return saved;
    },
    computeNetPrice(value) {
      if (value === null || value === undefined || value === "") return null;
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return null;
      return numeric + NET_PRICE_ADJUSTMENT;
    },
    formatNetPrice(value) {
      const net = this.computeNetPrice(value);
      if (net === null) return "—";
      return this.formatPrice(net);
    },
    formatVatPrice(value) {
      const net = this.computeNetPrice(value);
      if (net === null) return "—";
      const gross = net * 1.24;
      return this.formatPrice(gross);
    },
    formatPrice(value, options = {}) {
      if (value === null || value === undefined || value === "") return "—";
      let numeric = Number(value);
      if (Number.isNaN(numeric)) return value;
      if (options.applyNetAdjustment) {
        numeric += NET_PRICE_ADJUSTMENT;
      }
      return new Intl.NumberFormat("et-EE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
      }).format(numeric);
    },
    resetPagination() {
      this.displayedCount = 30;
    },
    setupObserver() {
      if (!this.hasSearched) {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        return;
      }
      this.$nextTick(() => {
        if (!this.$refs.loadSentinel) {
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
            }
          return;
        }
        if (this.observer) {
          this.observer.disconnect();
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.displayedCount = Math.min(
                  this.displayedCount + 10,
                  this.filteredItems.length
                );
              }
            });
          },
          {
            root: null,
            rootMargin: "0px 0px 200px 0px",
            threshold: 0.1,
          }
        );
        this.observer.observe(this.$refs.loadSentinel);
      });
    },
    handleScroll() {
      if (!this.hasSearched || this.loading) return;
      if (!this.hasMoreItems) return;
      const threshold = document.documentElement.offsetHeight - 200;
      if (window.innerHeight + window.scrollY >= threshold) {
        this.displayedCount = Math.min(
          this.displayedCount + 10,
          this.filteredItems.length
        );
      }
    },
  },
  beforeUnmount() {
    this.saveCurrentSearch();
    if (this.viewportMediaQuery) {
      const handler = this.handleViewportChange;
      if (typeof this.viewportMediaQuery.removeEventListener === "function") {
        this.viewportMediaQuery.removeEventListener("change", handler);
      } else if (typeof this.viewportMediaQuery.removeListener === "function") {
        this.viewportMediaQuery.removeListener(handler);
      }
      this.viewportMediaQuery = null;
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    window.removeEventListener("scroll", this.handleScroll);
  },
  watch: {
    hasSearched(newValue) {
      if (!newValue) {
        this.filtersVisible = true;
      } else {
        this.applyViewportFilterState();
      }
    },
    filteredItems() {
      if (!this.hasSearched) {
        this.resetPagination();
        this.setupObserver();
        return;
      }
      this.resetPagination();
      this.setupObserver();
    },
  },
};
</script>

<style scoped>
/* The tyre placeholder sits under each thumbnail and is hidden once the
   product image has loaded (or stays visible if it fails). */
.thumb.is-loaded .thumb-fallback {
  display: none;
}
</style>
